import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { RegisterWithPIRequest, ValidateInvitationResponse } from "../types/api";
import { translateApiError } from "../i18n/apiError";

interface RegisterProps {
  onRegisterSuccess: (email: string) => void;
  onRequires2FASetup?: () => void;
  onInvitationLoginSuccess?: () => void;
}

export const Register: React.FC<RegisterProps> = ({
  onRegisterSuccess,
  onRequires2FASetup,
  onInvitationLoginSuccess,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("register");
  const [formData, setFormData] = useState<RegisterWithPIRequest>({
    email: "",
    name: "",
    institution: "",
    password: "",
    pi_email: "",
    project_title_description: "",
  });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Read the invitation token synchronously so we don't flash the "invitation only"
  // screen before the mount effect runs.
  const initialToken =
    new URLSearchParams(window.location.search).get("token") ||
    (location.state as any)?.token ||
    null;
  const [invitationToken, setInvitationToken] = useState<string | null>(initialToken);
  const [invitationInfo, setInvitationInfo] = useState<ValidateInvitationResponse | null>(null);
  const [validatingInvitation, setValidatingInvitation] = useState<boolean>(!!initialToken);

  // Check for invitation token in URL or location state on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token") || (location.state as any)?.token;

    if (token) {
      setInvitationToken(token);
      setValidatingInvitation(true);

      // Validate the invitation token
      apiService
        .validateInvitation(token)
        .then((info) => {
          setInvitationInfo(info);
          // Pre-fill email and institution from invitation
          if (info.email || info.organization_name) {
            setFormData((prev) => ({
              ...prev,
              email: info.email || prev.email,
              institution: info.organization_name || prev.institution,
            }));
          }
          setValidatingInvitation(false);
        })
        .catch((err) => {
          setError(translateApiError(t, err));
          setValidatingInvitation(false);
          setInvitationToken(null);
        });
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (
      !formData.email ||
      !formData.name ||
      !formData.institution ||
      !formData.password
    ) {
      setError(t("errors.missingFields"));
      return;
    }

    // For self-registration (no invitation), PI email is required
    if (!invitationToken && !formData.pi_email) {
      setError(t("errors.piEmailRequired"));
      return;
    }

    if (formData.password !== confirmPassword) {
      setError(t("errors.passwordMismatch"));
      return;
    }

    if (formData.password.length < 8) {
      setError(t("errors.passwordTooShort"));
      return;
    }

    try {
      setLoading(true);

      // Build registration request
      const request: RegisterWithPIRequest = {
        email: formData.email,
        name: formData.name,
        institution: formData.institution,
        password: formData.password,
        pi_email: formData.pi_email,
        project_title_description: formData.project_title_description || undefined,
        invitation_token: invitationToken || undefined,
      };

      const response = await apiService.register(request);

      if (response.success) {
        if (invitationToken && response.access_token) {
          // Invitation-based registration: auto-logged in, go directly to 2FA setup
          if (response.requires_2fa_setup && onRequires2FASetup) {
            onRequires2FASetup();
          } else if (onInvitationLoginSuccess) {
            onInvitationLoginSuccess();
          } else {
            navigate("/projects", { replace: true });
          }
        } else if (invitationToken) {
          // Invitation-based registration but no token returned: go to login
          navigate("/login", {
            state: { message: response.message },
            replace: true,
          });
        } else {
          // Self-registration: needs email verification
          localStorage.setItem("registration_email", formData.email);
          onRegisterSuccess(formData.email);
        }
      }
    } catch (err) {
      setError(translateApiError(t, err));
    } finally {
      setLoading(false);
    }
  };

  if (validatingInvitation) {
    return (
      <div className="max-w-lg mx-auto mt-12 px-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="px-8 pt-8 pb-6">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {t("validating.title")}
            </h2>
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Registration is by invitation only, and the only entry point is an invitation link
  // (which carries a token). Without a token there is nothing to do here — send visitors
  // to the login page. The token-based invitation flow below stays intact.
  if (!validatingInvitation && !invitationToken) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="max-w-lg mx-auto mt-12 px-4">
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="px-8 pt-8 pb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {invitationInfo ? t("titleInvitation") : t("titleDefault")}
          </h2>

          {invitationInfo && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                {t("invitationBanner.prefix")}{" "}
                <span className="font-semibold capitalize">
                  {invitationInfo.role.replace(/_/g, " ")}
                </span>
                {" "}{t("invitationBanner.forOrganization")}{" "}
                <span className="font-semibold">
                  {invitationInfo.organization_name || t("invitationBanner.defaultOrganization")}
                </span>
              </p>
            </div>
          )}

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("form.emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled={loading || !!invitationInfo?.email}
                readOnly={!!invitationInfo?.email}
                required
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100 ${invitationInfo?.email ? "bg-gray-50 text-gray-600" : ""}`}
              />
              {invitationInfo?.email ? (
                <p className="mt-1 text-xs text-gray-500">
                  {t("form.emailInvitationLocked")}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  {t("form.emailHint")}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("form.nameLabel")}
              </label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                disabled={loading}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            <div>
              <label
                htmlFor="institution"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("form.institutionLabel")} {invitationToken && invitationInfo?.organization_name && <span className="text-xs text-gray-500">{t("form.fromInvitation")}</span>}
              </label>
              <input
                id="institution"
                name="institution"
                type="text"
                value={formData.institution}
                onChange={handleChange}
                disabled={loading || (invitationToken && invitationInfo?.organization_name ? true : false)}
                required
                placeholder={t("form.institutionPlaceholder")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                {invitationToken && invitationInfo?.organization_name
                  ? t("form.institutionInvitationLocked")
                  : t("form.institutionHint")}
              </p>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("form.passwordLabel")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                required
                minLength={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                {t("form.passwordHint")}
              </p>
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t("form.confirmPasswordLabel")}
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-gray-100"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-all disabled:bg-gray-400 disabled:cursor-not-allowed mt-2"
            >
              {loading ? t("submitting") : t("submit")}
            </button>

            <button
              type="button"
              onClick={() => navigate("/login")}
              disabled={loading}
              className="w-full py-2 bg-white hover:bg-gray-50 text-primary border border-primary rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("signInLink")}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
